import { useAuth } from "@clerk/clerk-react";
import { LoadingButton } from "@mui/lab";
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Grid,
  Link,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import { useEffect, useState } from "react";
import {
  AddOnItemName,
  OrganizationProfile,
  OrganizationUser,
  TemplateIntakeForm,
  TemplateIntakeFormStatus,
} from "schemas/dashboard";
import { SetAlertType } from "schemas/functions";

import { fetcherAuth } from "utils/api";
import { titleCase } from "utils/string";
import { formatEpochTimestamp } from "utils/time";

import { AdminAddOnItem } from "./schema";

interface Props {
  setAlert: SetAlertType;
  organizationUser: OrganizationUser | null;
}

type BackgroundDetails = {
  question: string;
  answer: string;
};

const AdminAddOns = ({ organizationUser, setAlert }: Props) => {
  const { getToken } = useAuth();
  const [userPurchases, setUserPurchases] = useState<AdminAddOnItem[]>([]);
  const [loading, setLoading] = useState(false);

  const [emailLoading, setEmailLoading] = useState(false);
  const [openIntakeDialog, setOpenIntakeDialog] = useState(false);
  const [intakeForm, setIntakeForm] = useState<TemplateIntakeForm | null>(null);
  const [backgroundDetails, setBackgroundDetails] = useState<
    BackgroundDetails[]
  >([]);
  const [orgProfile, setOrgProfile] = useState<OrganizationProfile | null>(
    null,
  );

  const fetchUserPurchases = async () => {
    if (!organizationUser) return;
    try {
      setLoading(true);
      const res = await fetcherAuth(
        getToken,
        `/api/admin/organizations/${organizationUser?.organizationId}/add-ons`,
        "GET",
      );

      setUserPurchases(res.userPurchases);
    } catch (error) {
      setAlert(error.message || "Unable to fetch user purchases", "error");
    } finally {
      setLoading(false);
    }
  };

  const sendTemplateCompletedEmail = async () => {
    try {
      setEmailLoading(true);
      await fetcherAuth(
        getToken,
        `/api/admin/organizations/${organizationUser?.organizationId}/add-ons/send-completed-template-email`,
        "POST",
      );
      setAlert("Successfully sent email.", "success");
      fetchUserPurchases();
    } catch (error) {
      setAlert(error.message || "Unable to send email", "error");
    } finally {
      setEmailLoading(false);
    }
  };

  const fetchUserBackgroundDetails = async () => {
    try {
      const res = await fetcherAuth(
        getToken,
        `/api/admin/organizations/${organizationUser?.organizationId}/background-details`,
        "GET",
      );
      setBackgroundDetails(res.backgroundDetails);
      setOrgProfile(res.organizationProfile);
    } catch (error) {}
  };

  useEffect(() => {
    if (organizationUser?.id) {
      fetchUserBackgroundDetails();
      fetchUserPurchases();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [organizationUser?.id]);
  return (
    <>
      <Typography variant="h6">Add-Ons Purchases</Typography>

      <LoadingButton
        size="small"
        variant="contained"
        disableElevation
        loading={emailLoading}
        sx={{ mb: 1 }}
        onClick={sendTemplateCompletedEmail}
      >
        Send Template Completed Email
      </LoadingButton>

      {loading ? (
        <CircularProgress />
      ) : (
        <TableContainer component={Paper} sx={{ maxWidth: 800, mt: 3 }}>
          <Table size="small" aria-label="simple table">
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontSize: 14, fontWeight: 600 }}>
                  Date Purchased
                </TableCell>
                <TableCell sx={{ fontSize: 14, fontWeight: 600 }}>
                  Item Name
                </TableCell>
                <TableCell sx={{ fontSize: 14, fontWeight: 600 }}>
                  Quantity
                </TableCell>
                <TableCell sx={{ fontSize: 14, fontWeight: 600 }}>
                  Purchase Details
                </TableCell>
                <TableCell sx={{ fontSize: 14, fontWeight: 600 }}>
                  Domain Info
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {userPurchases.map((row) => (
                <TableRow
                  key={row.id}
                  sx={{ "&:last-child td, &:last-child th": { border: 0 } }}
                >
                  <TableCell component="th" scope="row">
                    {formatEpochTimestamp({
                      epochTimestamp: row?.createdAt,
                      fullDate: true,
                    })}
                  </TableCell>
                  <TableCell>{titleCase(row.itemName)}</TableCell>
                  <TableCell>{row.quantity}</TableCell>
                  <TableCell>
                    {row.itemName === AddOnItemName.pitching_templates ? (
                      <Grid>
                        {row?.templateIntakeForms?.map((form, index) => (
                          <Box key={index}>
                            Template {index + 1} is{" "}
                            {form.status ===
                            TemplateIntakeFormStatus.pending ? (
                              <>pending 🟡</>
                            ) : (
                              <>completed ✅</>
                            )}
                            <br />{" "}
                            <Link
                              sx={{ cursor: "pointer" }}
                              onClick={() => {
                                setOpenIntakeDialog(true);
                                setIntakeForm(form);
                              }}
                            >
                              View User Intake{" "}
                            </Link>
                          </Box>
                        ))}
                      </Grid>
                    ) : (
                      <>--</>
                    )}
                  </TableCell>
                  <TableCell>
                    {row?.domainInfo ? (
                      <>
                        Email: {row.domainInfo.username}@{row.domainInfo.domain}
                        <br />
                        First Name: {row.domainInfo.first_name}
                        <br />
                        Last Name: {row.domainInfo.last_name}
                      </>
                    ) : (
                      "--"
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      <Dialog
        open={openIntakeDialog}
        onClose={() => {
          setOpenIntakeDialog(false);
          setIntakeForm(null);
        }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ textAlign: "center" }}>
          User Intake For Pending Template{" "}
        </DialogTitle>

        <DialogContent sx={{ mt: -3 }}>
          <Divider sx={{ my: 3, mx: -3 }} />
          <Typography variant="h6">General Information </Typography>

          <ul>
            {orgProfile?.location && <li>Location: {orgProfile?.location} </li>}
            {orgProfile?.city && <li>City: {orgProfile?.city} </li>}
            {orgProfile?.portfolioUrl && (
              <li>
                Portfolio:{" "}
                <Link href={orgProfile?.portfolioUrl} target="_blank">
                  {orgProfile?.portfolioUrl}
                </Link>{" "}
              </li>
            )}
            {orgProfile?.instagramUrl && (
              <li>
                Instagram:{" "}
                <Link href={orgProfile?.instagramUrl} target="_blank">
                  {orgProfile?.instagramUrl}
                </Link>{" "}
              </li>
            )}
            {orgProfile?.tiktokUrl && (
              <li>
                TikTok:{" "}
                <Link href={orgProfile?.tiktokUrl} target="_blank">
                  {orgProfile?.tiktokUrl}
                </Link>{" "}
              </li>
            )}
            {orgProfile?.youtubeUrl && (
              <li>
                YouTube:{" "}
                <Link href={orgProfile?.youtubeUrl} target="_blank">
                  {orgProfile?.youtubeUrl}
                </Link>{" "}
              </li>
            )}
            {orgProfile?.twitchUrl && (
              <li>
                Twitch:{" "}
                <Link href={orgProfile?.twitchUrl} target="_blank">
                  {orgProfile?.twitchUrl}
                </Link>{" "}
              </li>
            )}
            {orgProfile?.numOfFollowers && (
              <li>Number of Followers: {orgProfile?.numOfFollowers}</li>
            )}
            {orgProfile?.introduction && (
              <li>Introduction: {orgProfile?.introduction}</li>
            )}
          </ul>

          <Divider sx={{ my: 3, mx: -3 }} />
          <Typography variant="h6">Intake Form Answers </Typography>

          <ul>
            <li>
              <strong>Notes</strong>: {intakeForm?.notes || "N/A"}
            </li>
            <li>
              <strong>Past Brands</strong>: {intakeForm?.pastBrands || ""}
            </li>
            <li>
              <strong>Share 2-3 unique facts about you</strong>:{" "}
              {intakeForm?.partnershipReason || "N/A"}
            </li>
            {intakeForm?.nicheName ? (
              <li>
                <strong>Write for this niche </strong>: {intakeForm?.nicheName}
              </li>
            ) : (
              <li>
                <strong>Template Type</strong>: Generic
              </li>
            )}
          </ul>

          <Divider sx={{ my: 3, mx: -3 }} />
          <Typography variant="h6">Background Details </Typography>
          <ul>
            {backgroundDetails.map((detail, index) => (
              <li key={index}>
                <strong>{detail.question}</strong> <br />{" "}
                {detail.answer || "N/A"}
              </li>
            ))}
          </ul>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setOpenIntakeDialog(false);
              setIntakeForm(null);
            }}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default AdminAddOns;
